import { faCircleCheck, faEllipsisVertical } from "@fortawesome/pro-solid-svg-icons";
import { Box, IconButton, Link, Stack, SxProps, Theme, Typography } from "@mui/material";
import MuiMenu from "@mui/material/Menu";
import {
  Children,
  cloneElement,
  createContext,
  ElementType,
  isValidElement,
  PropsWithChildren,
  ReactElement,
  useCallback,
  useContext,
  useState,
} from "react";
import { sr25Blue } from "../../../theming/sr25Colors";
import { TILE_HEIGHT, TILE_INFO_MIN_HEIGHT } from "../../../utils/constants";
import { SrMediaThumbnail } from "../../Media/SrMediaThumbnail/SrMediaThumbnail";
import { useMenu } from "../../Navigation/Menu/useMenu";
import { FaIcon } from "../Icon/FaIcon";

const SR_TILE_SELECTION_INDICATOR_SIZE = 24;

type SrTileLinkContextValue = {
  url?: string;
  onClick?: () => void;
  isContextMenuOpen?: boolean;
  // Track hover state of the entire tile
  isHovered?: boolean;
  linkComponent?: ElementType;
};

const SrTileLinkContext = createContext<SrTileLinkContextValue>({});

/** Whether the enclosing `SrTile` is hovered; false outside one. */
export const useIsSrTileHovered = (): boolean => {
  const { isHovered = false } = useContext(SrTileLinkContext);

  return isHovered;
};

const useSrTileLinkClick = () => {
  const { isContextMenuOpen, onClick } = useContext(SrTileLinkContext);

  return useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (isContextMenuOpen) {
        event.preventDefault();
      }
      if (onClick) {
        // We call the passed onClick but prevent the default link navigation
        // if the consumer wants to handle it themselves.
        onClick();
        event.preventDefault();
      }
    },
    [isContextMenuOpen, onClick],
  );
};

/**
 * Resolves the props for the tile's anchors.
 *
 * The destination is always passed as `href` so that the default rendering is a
 * plain anchor and this package never imports a router. Consumers that want
 * client-side navigation pass an anchor-shaped `linkComponent` — see
 * `RouterLinkBehavior` in `@shuttlerock/router`.
 */
const useSrTileLinkProps = () => {
  const { linkComponent, url } = useContext(SrTileLinkContext);
  const onClick = useSrTileLinkClick();

  return {
    component: linkComponent,
    href: url || "",
    onClick,
  };
};

export type SrTileProps = {
  selected?: boolean;
  disabled?: boolean;
  url?: string;
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
  onClick?: () => void;
  isContextMenuOpen?: boolean;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  openContextMenuAriaLabel?: string;
  onMouseEnter?: () => void;
  /**
   * Anchor-shaped component used to render the tile's links. Defaults to a
   * plain `<a>`, which triggers a full page load — pass `RouterLinkBehavior`
   * from `@shuttlerock/router` for client-side navigation.
   */
  linkComponent?: ElementType;
};

/*
 * SrTile root component
 */
export const SrTile = ({
  selected,
  disabled,
  url,
  children,
  sx,
  onClick,
  isContextMenuOpen,
  onMenuOpen,
  onMenuClose,
  openContextMenuAriaLabel = "Open the context menu",
  onMouseEnter,
  linkComponent,
}: SrTileProps): ReactElement => {
  // Track hover for the whole tile
  const [isHovered, setIsHovered] = useState(false);

  const linkContextValue = {
    url,
    onClick,
    isContextMenuOpen,
    isHovered,
    linkComponent,
  };

  return (
    <Stack
      sx={[
        {
          position: "relative",
          width: "100%",
          minHeight: {
            sm: TILE_HEIGHT,
          },
          borderRadius: (theme) => `${theme.shape.borderRadius}px`,
          "& p": {
            my: 0.6,
          },
          backgroundColor: selected ? "surface.overlay" : "unset",
          transitionProperty: "background-color",
          transitionDuration: (theme) => `${theme.transitions.duration.interactiveStateOut}ms`,
          border: 0,
          boxShadow: selected ? "0 0 0 1px var(--sr-palette-stroke-active) inset" : "none",
          "& .preview": {
            backgroundColor: selected && "transparent",
            transitionProperty: "background-color",
            transitionDuration: (theme) => `${theme.transitions.duration.interactiveStateOut}ms`,
          },
          ...(!disabled && {
            "&:hover, &:has([aria-expanded='true']), &:focus-within": {
              backgroundColor: "surface.overlay",
              transitionDuration: (theme) => `${theme.transitions.duration.interactiveStateIn}ms`,
              "& .badge": {
                opacity: 0,
              },
              "& .context-menu": {
                opacity: 1,
                transitionDuration: (theme) => `${theme.transitions.duration.interactiveStateIn}ms`,
              },
              "& .preview": {
                backgroundColor: "transparent",
                transitionDuration: (theme) => `${theme.transitions.duration.interactiveStateIn}ms`,
              },
              "& h5": {
                color: "primary.light",
              },
            },
          }),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      // Manage tile-wide hover state
      onMouseEnter={() => {
        setIsHovered(true);
        onMouseEnter?.();
      }}
      onMouseLeave={() => setIsHovered(false)}
    >
      <SrTileLinkContext.Provider value={linkContextValue}>
        {!disabled && selected !== undefined && (
          <Box
            sx={{
              "--check-circle-stroke-color": isHovered
                ? "var(--sr-palette-stroke-hover)"
                : "var(--sr-palette-stroke-main)",
              position: "absolute",
              top: 8,
              left: 8,
              zIndex: 1,
              borderRadius: (theme) => `${theme.shape.borderRadiusMax}px`,
              backgroundColor: "surface.main",
              boxShadow: "0 0 0 1px var(--check-circle-stroke-color) inset",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: SR_TILE_SELECTION_INDICATOR_SIZE,
              height: SR_TILE_SELECTION_INDICATOR_SIZE,
              pointerEvents: "none", // Prevents the checkmark from blocking clicks on the tile
            }}
          >
            <FaIcon
              fontSize="large"
              icon={faCircleCheck}
              sx={{
                visibility: selected ? "visible" : "hidden",
                color: "primary.main",
              }}
            />
          </Box>
        )}
        {Children.map(children, (child) => {
          if (isValidElement(child)) {
            // Pass menu state handlers to children
            // Only some children need the handlers
            return cloneElement<any>(child, {
              onMenuOpen,
              onMenuClose,
              openContextMenuAriaLabel,
            });
          }
          return child;
        })}
      </SrTileLinkContext.Provider>
    </Stack>
  );
};

/*
 * Badge
 */
export type SrBadgeProps = {
  sx?: SxProps<Theme>;
};

const Badge = ({ sx, children }: PropsWithChildren<SrBadgeProps>): React.JSX.Element => {
  return (
    <Box
      className="badge"
      sx={[
        {
          position: "absolute",
          top: 0,
          opacity: 1,
          px: 1,
          pt: 0.25,
          pb: 0.5,
          mt: 1,
          ml: 1,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          // Keeps a block-level label (a `Typography` without `component="span"`)
          // on the same line as its icon.
          whiteSpace: "nowrap",
          borderRadius: (theme) => `${theme.shape.borderRadiusSm}px`,
          backgroundColor: "surface.main",
          transition: "opacity 0.2s",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Box>
  );
};

SrTile.Badge = Badge;

/*
 * Preview
 */
export type SrPreviewProps = {
  previewUrl?: string | null;
  previewContentType?: string | null;
  /** Poster image shown for a video preview before it plays */
  previewThumbnailUrl?: string | null;
  /** Blurs the media and holds video playback while edits are being applied */
  isApplyingEdits?: boolean;
  sx?: SxProps<Theme>;
};

const Preview = ({
  previewUrl,
  previewContentType,
  previewThumbnailUrl,
  isApplyingEdits,
  sx,
  children,
}: PropsWithChildren<SrPreviewProps>): React.JSX.Element => {
  const isHovered = useIsSrTileHovered();
  const linkProps = useSrTileLinkProps();
  const resolvedIsVideo = previewContentType?.startsWith("video/") ?? false;

  return (
    <Link
      className="preview"
      {...linkProps}
      sx={[
        {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textDecoration: "none",
          width: "100%",
          height: TILE_HEIGHT,
          p: 2,
          borderRadius: (theme) => `${theme.shape.borderRadius}px`,
          backgroundColor: "surface.overlay",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {previewUrl && (
        <SrMediaThumbnail
          url={previewUrl}
          thumbnail={previewThumbnailUrl ?? undefined}
          isVideo={resolvedIsVideo}
          isOverlayVisible={isHovered}
          // Active when tile is hovered and content is a video
          isActive={resolvedIsVideo && isHovered}
          isApplyingEdits={isApplyingEdits}
        />
      )}
      {children}
    </Link>
  );
};

SrTile.Preview = Preview;

/*
 * Info
 */
export type SrInfoProps = {
  title?: string | null;
  sx?: SxProps<Theme>;
  contextMenuItems?: React.JSX.Element[];
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  openContextMenuAriaLabel?: string;
};

const Info = ({
  title,
  sx,
  children,
  contextMenuItems,
  onMenuOpen,
  onMenuClose,
  openContextMenuAriaLabel,
}: PropsWithChildren<SrInfoProps>): React.JSX.Element => {
  const { triggerProps, menuProps } = useMenu("Tile Context Menu");
  const linkProps = useSrTileLinkProps();

  const handleTriggerClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    triggerProps.onClick?.(event);
    onMenuOpen?.();
  };

  const handleMenuClose = () => {
    menuProps.onClose();
    onMenuClose?.();
  };

  return (
    <Box
      className="info"
      sx={[
        {
          display: "flex",
          width: "100%",
          minHeight: TILE_INFO_MIN_HEIGHT,
          p: 2,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        sx={{
          flex: "1 1 auto",
          minWidth: 0,
          gap: 0.25,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {title != null && title !== "" && (
          <Typography
            variant="h5"
            noWrap
            sx={{
              "& a": { color: "on.surface", fontWeight: "bold" },
              "&:hover a": { color: sr25Blue[500] },
            }}
          >
            <Link {...linkProps} sx={{ fontWeight: "bold" }}>
              {title}
            </Link>
          </Typography>
        )}
        <Typography
          component="div"
          variant="caption"
          sx={{
            flex: "1 0 auto",
            gap: 0.25,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            color: "on.surfaceDimmer",
          }}
        >
          {children}
        </Typography>
      </Box>
      {/* `!!`: an empty array's `length` is 0, which React renders as "0". */}
      {!!contextMenuItems?.length && (
        <Box
          className="context-menu"
          sx={{
            display: "flex",
            alignItems: "flex-end",
            opacity: 0,
            // Touch devices have no hover to reveal the trigger with.
            "@media (hover: none)": { opacity: 1 },
            transitionProperty: "opacity",
            transitionDuration: (theme) => `${theme.transitions.duration.interactiveStateOut}ms`,
          }}
        >
          <IconButton
            {...triggerProps}
            onClick={handleTriggerClick}
            aria-label={openContextMenuAriaLabel}
          >
            <FaIcon icon={faEllipsisVertical} />
          </IconButton>
          <MuiMenu
            {...menuProps}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {contextMenuItems}
          </MuiMenu>
        </Box>
      )}
    </Box>
  );
};

SrTile.Info = Info;
